import * as yaml from "js-yaml";
import { loggingAllowed, showLogInfo } from "src/constants/profileSettings";
import GeneratorEntity from "../models/Generator";
import Profile from "../models/Profile";
import store from "../store/store";

let localProfile = new Profile();

interface IGeneratorsResult {
  data?: {
    generators: GeneratorEntity[];
  };
  generators?: GeneratorEntity[];
}

interface IProfileResult {
  data?: {
    profile: Profile;
  };
  profile?: Profile;
}

interface IValidateResult {
  data: {
    htmlContent: string;
    htmlResult: string;
    result: string;
    textContent: string;
  };
}

function getGenerators(result: IGeneratorsResult): GeneratorEntity[] {
  return result?.data?.generators ?? result?.generators ?? [];
}

export enum logType {
  debug = "debug",
  error = "error",
  warning = "warning",
  action = "action",
  event = "event"
}

export enum logAction {
  clickHandler = "clickHandler",
  pageLoad = "pageLoad",
  settingschange = "settingschange",
  login = "login",
  logout = "logout",
  openedFolders = "openedFolders",
  openedCategories = "openedCategories"
}

export function loadGenerators() {
  const url = "generators";
  return apiRequest(url).then((result: IGeneratorsResult) => {
    const generators: GeneratorEntity[] = [];
    if (result !== undefined) {
      for (const generator of getGenerators(result)) {
        generator.hasAllData = false;
        generators.push(generator);
      }
    }
    return generators;
  });
}

export function deleteGeneratorRequest(id: string) {
  const method = "DELETE";
  const url = "generators/" + id;
  return apiRequest(url, method)
    .catch((error: Error) => {
      console.error("Error: ", error.message); // tslint:disable-line
      return false;
    })
    .then((result: any) => {
      return true;
    });
}

// EDITOR COMPONENTS

export function validateWithoutID(code: string) {
  const url = "generators/validate";
  return apiRequest(url, "POST", yaml.load(code))
    .catch((error: any) => {
      return;
    })
    .then((result: IValidateResult) => {
      if (result !== undefined) {
        return result.data;
      } else {
        return;
      }
    });
}

export function generatorWithoutID(code: string) {
  const url = "generators/generate";
  return apiRequest(url, "POST", code).then((result: any) => {
    if (result !== undefined) {
      return result.data;
    } else {
      return;
    }
  });
}

export function runGeneratorWithId(id: string) {
  const url = "generators/" + id + "/generate";
  return apiRequest(url, "GET").then((result: any) => {
    if (result !== undefined) {
      return result.data;
    } else {
      return;
    }
  });
}

export function saveNewGenerator(generator: any) {
  const url = "generators";
  const method = "POST";
  return apiRequest(url, method, generator)
    .catch(error => {
      return;
    })
    .then((result: IGeneratorsResult) => {
      return getGenerators(result);
    });
}

// Overwrite the generator with the
export function updateGenerator(generator: any) {
  const url = "generators/" + generator.id;
  const method = "PUT";
  return apiRequest(url, method, generator)
    .catch(error => {
      console.error(error); // tslint:disable-line
    })
    .then((result: IGeneratorsResult) => {
      if (result !== undefined) {
        return result.data ?? result.generators;
      } else {
        return;
      }
    });
}

export function loadGenDetails(id: string, callback?: any) {
  const url = "generators/" + id;
  return apiRequest(url).then((result: IGeneratorsResult) => {
    if (result !== undefined) {
      const generator: GeneratorEntity = getGenerators(result)[0];
      generator.hasAllData = true;
      if (callback) {
        callback(generator);
      }
      return generator;
    } else {
      return;
    }
  });
}

export function loadProfile() {
  const url = "profile";
  return apiRequest(url).then((result: IProfileResult) => {
    let profile;
    const defaultProfile = new Profile();
    if (result) {
      profile = result.data?.profile ?? result.profile ?? new Profile();
      try {
        if (!profile.showLogInfo) {
          profile.showLogInfo = showLogInfo.true;
        }
      } catch (error) {
        profile = defaultProfile;
      }

      if (profile === null) {
        profile = new Profile();
      }
      for (const entity in defaultProfile) {
        if (profile[entity] === null || profile[entity] === undefined) {
          profile[entity] = defaultProfile[entity];
        }
      }
    } else {
      profile = new Profile();
    }
    localStorage.setItem("locale", profile.appLanguage);
    localProfile = profile;

    return profile;
  });
}

export function uploadProfile() {
  const url = "profile";
  const method = "POST";
  return apiRequest(url, method, store.getState().user.profile).then((result: IProfileResult) => {
    const profile = result.data?.profile ?? result.profile ?? new Profile();
    localStorage.setItem("locale", profile.appLanguage || "en");
    // localStorage.setItem("loggingAllowed", profile.appLanguage || "en");
    localProfile.loggingAllowed = profile.loggingAllowed;
    return profile;
  });
}

export async function resetProfile() {
  const url = "profile";
  const method = "POST";
  await apiRequest(url, method, "")
  return
}

function getStackTrace() {
  let stack;

  try {
    throw new Error("");
  } catch (error) {
    stack = error.stack || "";
  }

  stack = stack.split("\n").map((line: any) => line.trim());
  return stack.splice(stack[0] === "Error" ? 2 : 1);
}

export function Log(lType: logType, lAction: logAction, lLocation: string, lMessage?: string) {
  if (localProfile.loggingAllowed === loggingAllowed.true) {
    const logObject = {
      action: lAction,
      clientId: "Generator UI",
      component: lLocation,
      message: lMessage,
      severity: lType,
      stackTrace: getStackTrace().join("\n")
    };
    saveLog(logObject);
  }
}

function saveLog(logObject: any) {
  const url = "log";
  const method = "POST";
  return apiRequest(url, method, logObject).then((result: JSON) => {
    // console.log(result); //tslint:disable-line
  });
}

export function requestValidation(generator: GeneratorEntity) {
  const url = "generators/" + generator.id + "/validate";
  return apiRequest(url).then((res: IValidateResult) => {
    return res.data;
  });
}

// 
// 
//  Public Token Management for Generators
// 
// 

export enum tokenFunctions {
  deleteToken = 'delete',
  getList = 'listAll',
  create = 'create',
  execute = 'execute',
}
export function tokenApiManager(endpoint: tokenFunctions, generator: GeneratorEntity, token: string = "") {
  let url = ""
  switch (endpoint) {
    case tokenFunctions.deleteToken:
      url = `generators/${generator.id}/execute-tokens/${token}`
      return apiRequest(url, "DELETE")
    case tokenFunctions.getList:
      url = `generators/${generator.id}/execute-tokens`
      return apiRequest(url)
    case tokenFunctions.create:
      url = `generators/${generator.id}/execute-tokens`;
      return apiRequest(url, "POST");
    case tokenFunctions.execute:
      url = `execute/${token}`
      return apiRequest(url, "POST", "", false)
  }
}



function apiRequest(url: string, method: string = "GET", payload: string = "", api2: boolean = true) {
  // let URL = `https://campaign-logger.com/generator/api2/${url}`
  let URL = `https://generator.campaign-logger.com/api2/${url}`;
  // let URL = `http://localhost:53606/api2/${url}`

  if (!api2) {
    // URL = `https://campaign-logger.com/generator/${url}`
    URL = `https://generator.campaign-logger.com/${url}`;
    // URL = `http://localhost:53606/${url}`
  }

  const token: string = store.getState().oidc.user.access_token;
  const headers: Headers = new Headers();
  let body: string = "";
  let options: {} = {};
  headers.append("Accept", "application/json");
  headers.append("Authorization", `Bearer ${token}`);

  if (method === "POST" || method === "PUT") {
    headers.append("Content-Type", "application/json");
    body = JSON.stringify(payload);
    options = {
      body,
      headers,
      method
    };
  } else {
    options = {
      headers,
      method
    };
  }

  return fetch(URL, options)
    .then(res => res.json())
    .then(data => ({ data }))
    .catch(error => {
      return error;
    }); // tslint:disable-line
}





// WEBPACK FOOTER //
// ./src/utils/api.ts